import {watch, type Ref} from 'vue';
import {useUserPreferencesStore, ColorScheme, MinFontSize, type ColorScheme as ColorSchemeType} from '@/ts/store/user-preferences.store';
import {storeToRefs} from 'pinia';
import {useQuasar} from 'quasar';
import {useAnalytics} from '@/ts/composables/stateful/use-analytics';

/** Provides access to the current color scheme and a setter to change it */
export function useTheme(): {
  colorScheme: Ref<ColorSchemeType>;
  setColorScheme(scheme: ColorSchemeType): void;
} {
  const userPreferencesStore = useUserPreferencesStore();
  const {colorScheme} = storeToRefs(userPreferencesStore);

  const setColorScheme = (scheme: ColorSchemeType): void => {
    colorScheme.value = scheme;
  };

  return {
    colorScheme,
    setColorScheme,
  };
}

/** Initializes watchers that sync color scheme and font size preferences to the DOM and Quasar */
export function useThemeSetup(): void {
  const userPreferencesStore = useUserPreferencesStore();
  const {colorScheme, minFontSize} = storeToRefs(userPreferencesStore);
  const $q = useQuasar();
  const analytics = useAnalytics();

  // Track color scheme changes
  watch(colorScheme, (value) => {
    document.documentElement.classList.remove('force-light-mode');
    document.documentElement.classList.remove('force-dark-mode');
    if (value === ColorScheme.dark) {
      document.documentElement.classList.add('force-dark-mode');
      $q.dark.set(true); // switch Quasar components
      analytics.trackEvent('color_scheme_changed', {scheme: 'dark'});
    } else if (value === ColorScheme.light) {
      document.documentElement.classList.add('force-light-mode');
      $q.dark.set(false); // switch Quasar components
      analytics.trackEvent('color_scheme_changed', {scheme: 'light'});
    } else {
      $q.dark.set('auto'); // switch Quasar components
      analytics.trackEvent('color_scheme_changed', {scheme: 'auto'});
    }
  }, {immediate: true});

  // Track font size changes
  watch(minFontSize, (value) => {
    document.documentElement.classList.remove('min-font-size-18', 'min-font-size-20');
    if (value === MinFontSize.large as number) {
      document.documentElement.classList.add('min-font-size-18');
      analytics.trackEvent('font_size_changed', {size: 'large'});
    } else if (value === MinFontSize.extraLarge as number) {
      document.documentElement.classList.add('min-font-size-20');
      analytics.trackEvent('font_size_changed', {size: 'extra_large'});
    } else {
      analytics.trackEvent('font_size_changed', {size: 'normal'});
    }
  }, {immediate: true});
}